import { useParams, Navigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Navigation } from "../components/configurator/Navigation";
import { HeroSection } from "../components/configurator/HeroSection";
import { Configurator } from "../components/configurator/Configurator";
import { ExplanatoryCards } from "../components/configurator/ExplanatoryCards";
import { Footer } from "../components/configurator/Footer";
import { Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConfigurateurPage() {
  const { brand } = useParams();
  const brandSlug = brand || "aerofoils";

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [sel, setSel] = useState({});
  const [pricing, setPricing] = useState(null);
  const [sku, setSku] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setConfig(null);
    setSel({});
    setStep(0);
    setPricing(null);
    setSku(null);
    axios.get(`${API}/config/${brandSlug}`)
      .then(r => {
        setConfig(r.data);
        const initSel = {};
        (r.data.steps || []).forEach(s => { initSel[s.id] = null; });
        setSel(initSel);
      })
      .catch(err => {
        console.error(err);
        setError(err.response?.status === 404 ? "Marque non trouvee" : "Erreur de chargement");
      })
      .finally(() => setLoading(false));
  }, [brandSlug]);

  const calcPrice = useCallback(async (s) => {
    if (!config) return;
    const steps = config.steps || [];
    const allFilled = steps.every(st => s[st.id]);
    if (!allFilled && config.pricing_mode !== "additive") {
      if (!s.serie || !s.batterie || !s.chargeur) { setPricing(null); return; }
    }
    if (config.pricing_mode === "additive") {
      const anyFilled = steps.some(st => s[st.id]);
      if (!anyFilled) { setPricing(null); return; }
    }
    try {
      const r = await axios.post(`${API}/calculate-price`, { brand: brandSlug, ...s });
      setPricing(r.data);
    } catch { /* ignore */ }
  }, [config, brandSlug]);

  const calcSku = useCallback(async (s) => {
    if (!config) return;
    const steps = config.steps || [];
    const allFilled = steps.every(st => s[st.id]);
    if (!allFilled) { setSku(null); return; }
    try {
      const r = await axios.post(`${API}/get-sku`, { brand: brandSlug, ...s });
      setSku(r.data.sku);
    } catch { /* ignore */ }
  }, [config, brandSlug]);

  useEffect(() => { calcPrice(sel); calcSku(sel); }, [sel, calcPrice, calcSku]);

  const isAerofoils = brandSlug === "aerofoils";

  const update = (k, v) => setSel(prev => {
    const n = { ...prev, [k]: v };
    if (isAerofoils) {
      if (k === "serie") { n.couleur = null; n.aile = null; n.batterie = null; n.chargeur = null; }
      else if (k === "couleur") { n.aile = null; n.batterie = null; n.chargeur = null; }
      else if (k === "aile") { n.batterie = null; n.chargeur = null; }
      else if (k === "batterie") { n.chargeur = null; if (v === "none") n.chargeur = "none"; }
    } else {
      const constraints = config?.constraints?.batterie || {};
      if (k === "board") {
        for (const [batId, c] of Object.entries(constraints)) {
          if ((c.excluded_when_board || []).includes(v) && prev.batterie === batId) {
            n.batterie = null;
          }
        }
      }
    }
    return n;
  });

  const steps = config?.steps || [];
  const totalSteps = steps.length;
  const scroll = () => document.getElementById("configurator")?.scrollIntoView({ behavior: "smooth" });

  if (!brand) return <Navigate to="/configurateur/aerofoils" replace />;

  if (loading) return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-[#1A3A1A] border-t-[#39FF14] rounded-full animate-spin"></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-500 text-xl mb-4">{error}</p>
        <a href="/configurateur/aerofoils" className="text-[#39FF14] underline">Retour au configurateur Aerofoils</a>
      </div>
    </div>
  );

  if (!config) return null;

  const isComplete = steps.length > 0 && steps.every(s => sel[s.id]);

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="configurateur-page">
      <Navigation onConfigureClick={scroll} brand={brandSlug} brandName={config.brand_name} />
      <HeroSection onScrollClick={scroll} content={config} />
      {totalSteps > 0 && (
        <section id="configurator" className="py-16 lg:py-24">
          <Configurator
            config={config}
            brand={brandSlug}
            currentStep={step}
            selection={sel}
            pricing={pricing}
            sku={sku}
            onStepChange={setStep}
            onSelectionChange={update}
            onNext={() => step < totalSteps - 1 && setStep(step + 1)}
            onPrev={() => step > 0 && setStep(step - 1)}
            isComplete={isComplete}
          />
        </section>
      )}
      <ExplanatoryCards currentStep={step} onCardClick={setStep} content={config} />
      <Footer content={config} />
      <Toaster position="bottom-right" />
    </div>
  );
}
